import { createChart, type Chart, type ChartOptions } from 'swp-charting';

/// Wrapper omkring swp-charting der:
///   1. Auto-discoverer alle <div class="chart-host"> + læser deres
///      data-chart-config fra inline <script type="application/json">.
///   2. Læser CSS-vars (--accent, --info, --purple) for farver så charts
///      auto-matcher current tema.
///   3. Lytter på `theme-changed`-event fra ThemeService og re-initialiserer
///      charts med opdateret palette.
///   4. Holder styr på instances så de kan destroy'es ved theme-skift.

interface ChartHostConfig {
  type: 'line' | 'donut';
  /// Generic chart-options-data. Vores Razor-side serialiserer this fra C#.
  data: unknown;
}

interface IncomingChartData {
  type: 'line';
  points: { hour: string; count: number }[];
}

interface DonutChartData {
  type: 'donut';
  slices: { label: string; value: number }[];
}

export class ChartService {
  private instances = new Map<string, Chart>();
  private palette: Palette;

  constructor(private readonly doc: Document = document) {
    // VIGTIGT: palette må først læses EFTER `this.doc` er tildelt — derfor
    // i constructor-body, ikke som field-initializer. Field-initializers
    // kører før constructor-parameters tildeles til parameter-property-
    // felter, så `this.doc` er undefined hvis vi prøver at læse palette
    // som default field-value.
    this.palette = this.readPalette();
    window.addEventListener('theme-changed', () => this.handleThemeChange());
  }

  /// Find alle .chart-host i dokumentet og initialisér dem.
  initAll(): void {
    this.doc.querySelectorAll<HTMLElement>('.chart-host').forEach(host => {
      this.initOne(host);
    });
  }

  private initOne(host: HTMLElement): void {
    const id = host.id;
    if (!id) {
      console.warn('chart-host mangler id', host);
      return;
    }
    const dataScript = this.doc.querySelector<HTMLScriptElement>(`script[data-chart-for="${id}"]`);
    if (!dataScript) {
      console.warn(`chart-host ${id} mangler data-script`);
      return;
    }

    let config: ChartHostConfig;
    try {
      config = JSON.parse(dataScript.textContent ?? '{}') as ChartHostConfig;
    } catch (e) {
      console.warn(`chart-host ${id} har invalid JSON-data`, e);
      return;
    }

    const opts = this.buildOptions(config);
    if (!opts) return;

    // Destroy eksisterende instans hvis vi re-initialiserer (theme-skift).
    this.instances.get(id)?.destroy();
    this.instances.set(id, createChart(host, opts));
  }

  private buildOptions(config: ChartHostConfig): ChartOptions | null {
    if (config.type === 'line') {
      const data = config.data as IncomingChartData;
      return {
        xAxis: { categories: data.points.map(p => p.hour) },
        series: [{
          name: 'Indkommende mails',
          type: 'line',
          color: this.palette.accent,
          data: data.points.map(p => ({ x: p.hour, y: p.count })),
          line: { width: 2, curve: 'smooth' },
          showArea: true,
          area: { gradient: { startOpacity: 0.25, endOpacity: 0.02 } },
          point: { style: 'dot', radius: 3 },
        }],
        yAxis: {
          min: 0,
          format: (v: number) => v.toLocaleString('da-DK'),
        },
        padding: { top: 20, right: 20, bottom: 30, left: 50 },
        tooltip: true,
      };
    }

    if (config.type === 'donut') {
      const data = config.data as DonutChartData;
      const colors = [
        this.palette.accent,
        this.palette.info,
        this.palette.purple,
        this.palette.warning,
        this.palette.danger,
        this.palette.muted,
      ];
      // swp-charting v0.3 fletter alle pie-series i én samlet donut (jf.
      // examples/index.html Chart 7). For multi-color skal vi derfor mappe
      // hver slice til sin egen series — hver med eget `color`-felt fra
      // palette-cyklus. Hvis input har flere slices end farver, cykler
      // paletten via modulo.
      return {
        xAxis: { categories: data.slices.map(s => s.label) },
        series: data.slices.map((s, i) => ({
          name: s.label,
          type: 'pie' as const,
          data: [{ x: s.label, y: s.value }],
          color: colors[i % colors.length],
          pie: { innerRadius: 60, outerRadius: 110, hoverOffset: 6 },
        })),
        legend: { position: 'right', align: 'center' },
        tooltip: {
          render: (point) => {
            const v = point.values[0];
            const gb = (v.value / (1024 * 1024 * 1024)).toFixed(1);
            return `<strong>${point.x}</strong>: ${gb} GB`;
          },
        },
      };
    }

    return null;
  }

  private handleThemeChange(): void {
    this.palette = this.readPalette();
    // Re-build alle charts med ny palette.
    this.doc.querySelectorAll<HTMLElement>('.chart-host').forEach(host => this.initOne(host));
  }

  private readPalette(): Palette {
    const cs = getComputedStyle(this.doc.documentElement);
    const get = (name: string) => cs.getPropertyValue(name).trim();
    return {
      accent: get('--accent') || '#a3e635',
      info: get('--info') || '#60a5fa',
      purple: get('--purple') || '#c084fc',
      warning: get('--warning') || '#fbbf24',
      danger: get('--danger') || '#f87171',
      muted: get('--text-dim') || '#5c6370',
    };
  }
}

interface Palette {
  accent: string;
  info: string;
  purple: string;
  warning: string;
  danger: string;
  muted: string;
}
